import React, { useEffect, useLayoutEffect, useState, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { apiRequest } from '../api';
import { ABG } from './abg';
import API_BASE_URL from '../apiConfig';
import './PokerGame.css';
import audioManager from './AudioManager'; 
import { commonIcons, pokerAssets, getFlag } from './assetImports';
import { useTranslation } from 'react-i18next';

const PokerGame = ({ onRematch }) => {
    const { t } = useTranslation('games');
    const { socket, user, updateUserDetails } = useContext(AuthContext);

    // State variables
    const [game, setGame] = useState(null);
    const navigate = useNavigate();
    const [opponents, setOpponents] = useState([]);
    const [isGameEnd, setIsGameEnd] = useState(false);
    const [playerCards, setPlayerCards] = useState([]);
    const [opponentHand, setOpponentHand] = useState([]);
    const [isMoveLoading, setIsMoveLoading] = useState(false);
    const [showActionButtons, setShowActionButtons] = useState(false);
    const [currentRound, setCurrentRound] = useState(1);
    const [popupMessage, setPopupMessage] = useState('');
    const [isWaitingForRematch, setIsWaitingForRematch] = useState(false);
    const [isSoundOn, setIsSoundOn] = useState(true); 
    const DEFAULT_VOLUME = 0.8;
    
    const [pokerShowTextMenu, setPokerShowTextMenu] = useState(false);
    const [pokerShowSettingsMenu, setPokerShowSettingsMenu] = useState(false);
    const [pokerPlayerMessage, setPokerPlayerMessage] = useState(null);
    const [pokerOpponentMessage, setPokerOpponentMessage] = useState(null);
    const [isLeaveGamePopupVisible, setIsLeaveGamePopupVisible] = useState(false);
    const [communityCards, setCommunityCards] = useState([]);
    const [displayedPot, setDisplayedPot] = useState(0);
    const [currentBet, setCurrentBet] = useState(0);
    const [playerStack, setPlayerStack] = useState(0);
    const [gamePhase, setGamePhase] = useState('waiting');
    const [isPlayerTurn, setIsPlayerTurn] = useState(false);
    const [isDealing, setIsDealing] = useState(false);
    const [showBonusDetails, setShowBonusDetails] = useState(false);

    const [isAnimatingBets, setIsAnimatingBets] = useState(false);

    const chipLayerRef = useRef(null);
    const potRef = useRef(null);           
    const playAreaRef = useRef(null);       

    const playerStackRef = useRef(null);    
    const opponentStackRef = useRef(null);

    const [animatedChips, setAnimatedChips] = useState([]); 
    const animatedChipsRef = useRef([]);                    
    const [showRaiseControl, setShowRaiseControl] = useState(false);
    const [raiseAmount, setRaiseAmount] = useState(0);
    const [raiseControllerPos, setRaiseControllerPos] = useState({ left: null, top: null });
    
    
    

    const chipIdRef = useRef(1);         

    // prev-bet refs to detect increments
    const prevPlayerBetRef = useRef(0);
    const prevOpponentBetRef = useRef(0);

    // prev phase to detect phase transitions
    const prevPhaseRef = useRef(null);

    useEffect(() => { animatedChipsRef.current = animatedChips; }, [animatedChips]);

    const createAnimatedChip = ({ owner, amount, startX, startY, targetX, targetY, duration = 600, stage = 'toBet' }) => {
  const id = chipIdRef.current++;
  // offset target to stack chips nicely per owner
  const sameStageCount = animatedChipsRef.current.filter(c => c.owner === owner && c.stage === stage).length;
  const offset = (owner === 'player' ? -18 : 18) * sameStageCount;
  const finalTargetX = Math.round(targetX + offset);
  const chip = {
    id, owner, amount,
    x: Math.round(startX), y: Math.round(startY),
    dx: 0, dy: 0,
    duration,
    stage,
    targetX: finalTargetX,
    targetY: Math.round(targetY)
  };

  setAnimatedChips(prev => [...prev, chip]);


  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      setAnimatedChips(prev => prev.map(c => c.id === id
        ? { ...c, dx: Math.round(c.targetX - c.x), dy: Math.round(c.targetY - c.y) }
        : c
      ));
    });
  });

  return id;
};

const animateBet = ({ owner, amount }) => {
  const stackRef = owner === 'player' ? playerStackRef : opponentStackRef;
  if (!stackRef?.current) return;

  

  const stackRect = stackRef.current.getBoundingClientRect();
  const tableRect =
    playAreaRef.current?.getBoundingClientRect() ??
    document.body.getBoundingClientRect();

  const tableCenterX = Math.round(tableRect.left + tableRect.width / 2);

  const handSelector =
    owner === 'player'
      ? '.poker-player-pokerHand'
      : '.poker-opponent-pokerHand';
  const handEl = document.querySelector(handSelector);

  let stagingY;
  if (handEl) {
    const handRect = handEl.getBoundingClientRect();
    stagingY =
      owner === 'player'
        ? Math.round(handRect.top + 10)
        : Math.round(handRect.bottom - 10);
  } else {
    const offsetY = owner === 'player' ? 40 : -40;
    stagingY = Math.round(tableRect.top + tableRect.height / 2) + offsetY;
  }

  const startX = Math.round(stackRect.left + stackRect.width / 2);
  const startY = Math.round(stackRect.top + stackRect.height / 2);
  const targetX = tableCenterX;

  const mainBetChip = animatedChipsRef.current.find(
    c => c.owner === owner && c.stage === 'toBet'
  );


  const spawnChip = (offsetX = 0, delay = 0, tempOnly = false) => {
    const id = chipIdRef.current++;
    const chip = {
      id,
      owner,
      amount: null, 
      x: startX + offsetX,
      y: startY,
      dx: 0,
      dy: 0,
      duration: 600,
      stage: 'increment',
      targetX,
      targetY: stagingY,
      tempOnly,
    };

    setAnimatedChips(prev => [...prev, chip]);

    setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimatedChips(prev =>
            prev.map(c =>
              c.id === id
                ? {
                    ...c,
                    dx: Math.round(c.targetX - c.x),
                    dy: Math.round(c.targetY - c.y),
                  }
                : c
            )
          );
        });
      });
    }, delay);


    setTimeout(() => {
      setAnimatedChips(prev => prev.filter(c => c.id !== id));
      
    }, 650 + delay);

    
  };

  if (mainBetChip) {
    if (amount > 20) {
   
      spawnChip(-5, 0, true);
      spawnChip(5, 80, true);
    } else {
      spawnChip(0, 0, true);
    }

    
    setTimeout(() => {
      setAnimatedChips(prev =>
        prev.map(c =>
          c.owner === owner && c.stage === 'toBet'
            ? { ...c, amount: c.amount + amount }
            : c
        )
      );
    }, 650);
  } else {
 
    createAnimatedChip({
      owner,
      amount,
      startX,
      startY,
      targetX,
      targetY: stagingY,
      duration: 600,
      stage: 'toBet',
    });
  }
  
};







const moveChipsToPot = (amountToAdd = 0) => {
  if (!potRef.current) return;

  
  const potRect = potRef.current.getBoundingClientRect();
  const potIcon = potRef.current.querySelector('.poker-chip-icon');
  const iconRect = potIcon ? potIcon.getBoundingClientRect() : potRect;
  const potX = Math.round(iconRect.left + iconRect.width / 2);
  const potY = Math.round(iconRect.top + iconRect.height / 2);

  const activeBetChips = animatedChipsRef.current.filter(c => c.stage === 'toBet');



  setAnimatedChips(prev => {
    const updated = prev.map(c => {
      if (c.stage !== 'toBet') return c;

      return {
        ...c,
        amount: 0, 
        dx: Math.round(potX - c.x),
        dy: Math.round(potY - c.y),
        stage: 'toPot',
        duration: 450,
      };
    });

    return updated;
  });


  const spawnTempChip = (baseChip, offsetX = 0, delay = 0) => {
    const id = chipIdRef.current++;
    const chip = {
      id,
      owner: baseChip.owner,
      amount: null,
      x: baseChip.x + offsetX,
      y: baseChip.y,
      dx: 0,
      dy: 0,
      duration: 450,
      stage: 'toPot',
      tempOnly: true,
      targetX: potX,
      targetY: potY,
    };

    
    setAnimatedChips(prev => {
      const newChips = [...prev, chip];
      return newChips;
    });

    setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimatedChips(prev =>
            prev.map(c =>
              c.id === id
                ? { ...c, dx: Math.round(potX - c.x), dy: Math.round(potY - c.y) }
                : c
            )
          );
        });
      });
    }, delay);

    setTimeout(() => {

      setAnimatedChips(prev => {
        const filtered = prev.filter(c => c.id !== id);
        return filtered;
      });
    }, chip.duration + delay + 60);
  };

  if (amountToAdd > 40 && activeBetChips.length) {
    activeBetChips.forEach(baseChip => {
      spawnTempChip(baseChip, -6, 0);
      spawnTempChip(baseChip, 6, 80);
    });
  } else if (activeBetChips.length) {
    activeBetChips.forEach(baseChip => spawnTempChip(baseChip, 0, 0));
  }


  setTimeout(() => {
    setAnimatedChips(prev => {
      const filtered = prev.filter(c => !(c.stage === 'toPot' || c.tempOnly));
      return filtered;
    });
    setDisplayedPot(prev => prev + amountToAdd);
  }, 520);
};

const [animatePlayerStack , setAnimatePlayerStack] = useState(false);
const [animateOpponentStack , setAnimateOpponentStack] = useState(false);



const animatePotToWinner = (winnerOwner = 'player', amount = 0) => {
  if (!potRef.current) return;

  const potRect = potRef.current.getBoundingClientRect();
  const potIcon = potRef.current.querySelector('.poker-chip-icon');
  const iconRect = potIcon ? potIcon.getBoundingClientRect() : potRect;
  const potX = Math.round(iconRect.left + iconRect.width / 2);
  const potY = Math.round(iconRect.top + iconRect.height / 2);


  const targetRef = winnerOwner === 'player' ? playerStackRef : opponentStackRef;
  if (!targetRef?.current) return;

  const targetRect = targetRef.current.getBoundingClientRect();
  const targetX = Math.round(targetRect.left + targetRect.width / 2);
  const targetY = Math.round(targetRect.top + targetRect.height / 2);

   if (winnerOwner === 'player') {
    setAnimatePlayerStack(true);
    setTimeout(() => setAnimatePlayerStack(false), 1000);
  } else {
    setAnimateOpponentStack(true);
    setTimeout(() => setAnimateOpponentStack(false), 1000);
  }

  setDisplayedPot(0);

  const spawnChip = (offsetX = 0, delay = 0) => {
    const id = chipIdRef.current++;
    const chip = {
      id,
      owner: 'pot',
      amount: null,
      x: potX + offsetX,
      y: potY,
      dx: 0,
      dy: 0,
      duration: 800,
      stage: 'toWinner',
      tempOnly: true,
      targetX,
      targetY,
    };

    setAnimatedChips(prev => [...prev, chip]);

    setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimatedChips(prev =>
            prev.map(c =>
              c.id === id
                ? {
                    ...c,
                    dx: Math.round(targetX - c.x),
                    dy: Math.round(targetY - c.y),
                  }
                : c
            )
          );
        });
      });
    }, delay);

    setTimeout(() => {
      setAnimatedChips(prev => prev.filter(c => c.id !== id));
    }, chip.duration + delay + 60);
  };


  if (amount > 40) {

    spawnChip(-6, 0);
    spawnChip(6, 80);
  } else {

    spawnChip(0, 0);
  }

  setTimeout(() => {
    setAnimatedChips(prev =>
      prev.filter(c => !(c.stage === 'toWinner' || c.tempOnly))
    );
  }, 1000); 
};

const lastActionRef = useRef(null);


useLayoutEffect(() => {
  if (!game || !game.lastAction) return;
  if (lastActionRef.current?.timestamp === game.lastAction.timestamp) return;

  const { type, playerId, amount, data } = game.lastAction;


  switch (type) {
    case 'blinds_posted':
      if (data?.blinds) {
        playPokerSound('placeBet');
        data.blinds.forEach(blind => {
          if (blind.amount > 0) {
            animateBet({
              owner: blind.playerId === user._id.toString() ? 'player' : 'opponent',
              amount: blind.amount,
            });
          }
        });
      }
      break;
    
    case 'raise':
    case 'call':
    case 'small_blind':
    case 'big_blind':
      if (amount > 0) {
        playPokerSound('placeBet');
        animateBet({
          owner: playerId === user._id.toString() ? 'player' : 'opponent',
          amount: amount,
        });
      }
      break;
    
    case 'fold':
    case 'check':

      break;
    
    default:
      break;
  }


  lastActionRef.current = game.lastAction;
  
  const prevPhase = prevPhaseRef.current;
  const currentPhase = game.phase;
  const phaseChanged = prevPhase && prevPhase !== currentPhase;

  if (phaseChanged) {
    const shouldDelay = (amount > 0 || game.pot > displayedPot) && game.phase !== 'pre-flop';
    
    if (shouldDelay) {
      setIsAnimatingBets(true);
      setTimeout(() => {
        if (game.pot > displayedPot) {
          playPokerSound('moveToPot');
          const amountToAdd = game.pot - displayedPot;
          moveChipsToPot(amountToAdd);
        }
        setCommunityCards(game.communityCards || []);
        setIsAnimatingBets(false);
      }, 1500);
    } else {
      setCommunityCards(game.communityCards || []);
    }
  }

  prevPhaseRef.current = currentPhase;
}, [game?.players, game?.phase, game?.lastAction]);




useEffect(() => {
  if (!game || !user) return;

  const opponentId = game.userIds.find((id) => id !== user._id);
  

  const bettingPhases = ['pre-flop', 'flop', 'turn', 'river'];
  const isPlayerTurn = game?.turn === user._id && bettingPhases.includes(game.phase);
 
  const isOpponentTurn = game?.turn === opponentId && bettingPhases.includes(game.phase);

  

  const playerTimer = document.querySelector('.poker-player-avatar .poker-timerP');
  const opponentTimer = document.querySelector('.poker-opponent-avatar .poker-timerO');
 
  if (playerTimer) {
    if (isPlayerTurn) {
      playerTimer.style.transition = `stroke-dashoffset ${25}s linear`;
      setTimeout(() => {
        playerTimer.style.strokeDashoffset = '0';
      }, 20); 
    } else {     
      playerTimer.style.strokeDashoffset = '226.2';
      playerTimer.style.transition = 'none';
    }
  }
  
  if (opponentTimer) {
    if (isOpponentTurn) {
      opponentTimer.style.transition = `stroke-dashoffset ${25}s linear`;
      setTimeout(() => {
        opponentTimer.style.strokeDashoffset = '0';
      }, 20); 
    } else {
      opponentTimer.style.strokeDashoffset = '226.2';
      opponentTimer.style.transition = 'none';
    }
  }
}, [game?.turn, game?.phase]);


useEffect(() => {
        const handleTouchStart = (event) => {
            event.preventDefault(); 
        };

        const handleTouchMove = (event) => {
            event.preventDefault();
          };
    
        const handleClickOutside = (event) => {
            event.preventDefault(); 
            const extendedButtons = document.querySelector('.poker-settings-expanded');
            const textDropdown = document.querySelector('.poker-dropdown-menu');
    
            if (extendedButtons && !extendedButtons.contains(event.target)) {
                setPokerShowSettingsMenu(false);
            }
    
            if (textDropdown && !textDropdown.contains(event.target)) {
                setPokerShowTextMenu(false);
            }
        };
    
        document.addEventListener("pointerdown", handleClickOutside, { passive: false });
        document.addEventListener("touchstart", handleTouchStart, { passive: false });
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
    
        return () => {
            document.removeEventListener("pointerdown", handleClickOutside);
            document.removeEventListener("touchstart", handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
        };
    }, []);
    




    useEffect(() => {
        audioManager.setSoundOn(isSoundOn);
        audioManager.setVolume(DEFAULT_VOLUME);
    }, [isSoundOn]);

    useEffect(() => {
        socket.on('game-replay', onRematch);
        return () => socket.off('game-replay', onRematch);
    }, [onRematch, socket]);

    useEffect(() => {
        const handleTouchStart = (event) => {
         
            if (event.touches.length > 1) {
                event.preventDefault();
            }
        };

        document.addEventListener("touchstart", handleTouchStart, { passive: false });

        return () => {
            document.removeEventListener("touchstart", handleTouchStart);
        };
    }, []);

   function playPokerSound(soundName , Valume = DEFAULT_VOLUME) {
        audioManager.setVolume(Valume);
        audioManager.playSound(soundName);
    }




    const fetchGameState = useCallback(async () => {
        try {
            const response = await apiRequest(`${API_BASE_URL}/api/current-game`, {
                method: 'GET',
                credentials: 'include',
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch game state');
            }
            
            const data = await response.json();

            if (data.game) {
                if (data.game.status === 'completed' || data.game.status === 'canceled') {
                    navigate('/game');
                    return; 
                }
            
                updateGameState(data.game, data.opponents || []);
            } else {
                setGame(null);
                setPopupMessage('Failed to fetch the current game state.');
                navigate('/game');
            }
        } catch (error) {
            console.error('Error fetching game state:', error);
            setGame(null);
            navigate('/game');
        }
    }, [navigate]);



const [lastActionTimestamp, setLastActionTimestamp] = useState(null);
useEffect(() => {
  if (!game || !game.lastAction) return;
  const action = game.lastAction;
  const opponent = game.players?.find(p => 
        p.userId.toString() !== user._id.toString()
        );
  

  if (action.type === 'check' && action.playerId.toString() === opponent.userId.toString()) {
       if (action.timestamp !== lastActionTimestamp) {
      setPokerOpponentMessage(t('poker.check'));
      setTimeout(() => setPokerOpponentMessage(null), 1200);

      setLastActionTimestamp(action.timestamp);
    }
  }
}, [game?.lastAction]);

    const updateGameState = useCallback((gameData, opponentsData) => {
        console.log("Updateeeeeeeeeeeeee",gameData)
        setGame(gameData);
        setOpponents(opponentsData);
        setCurrentRound(gameData.roundNumber || 1);
        
        const currentPlayer = gameData.players?.find(p => 
            p.userId.toString() === user._id.toString()
        );
         const opponent = gameData.players?.find(p => 
        p.userId.toString() !== user._id.toString()
        );
        
        const bettingPhases = ['pre-flop', 'flop', 'turn', 'river'];
        const isPlayerTurn = gameData?.turn === user._id && bettingPhases.includes(gameData.phase);
            setShowActionButtons(isPlayerTurn);
            setIsPlayerTurn(isPlayerTurn);


        // Update player-specific state
        if (currentPlayer) {
            setPlayerCards(currentPlayer.hand || []);
            setPlayerStack(currentPlayer.stack || 0);
        }

        if (opponent) {
            setOpponentHand(opponent.hand || []);
        }
        
        // Update game state
       
        setCurrentBet(gameData.currentBet || 0);
        setGamePhase(gameData.phase || 'waiting');
        
       
        
        
       
        
        // Handle game end
        if (gameData.status === 'completed') {
            setIsGameEnd(true);
        }
        
        // Handle different game phases with animations
        handleGamePhase(gameData, opponentsData);
    }, [user]);
 
 
   
   

    const handleGamePhase = useCallback((gameData, opponentsData) => {
        switch (gameData.phase) {
            case 'initial-deal':
                handleRoundStart(gameData);
                break;
            case 'pre-flop':
                handlePreFlopPhase(gameData, opponentsData);
                break;
            case 'flop':
                handleFlopPhase(gameData);
                break;
            case 'turn':
                handleTurnPhase(gameData);
                break;
            case 'river':
                handleRiverPhase(gameData);
                break;
            case 'showdown':
                handleShowdownPhase(gameData);
                break;
            case 'round-complete':
                handleRoundEndPhase(gameData);
                break;
         
            default:
                // Handle other phases or reset states
                setIsDealing(false);
                break;
        }
    }, []);


    const handlePreFlopPhase = useCallback((gameData, opponentsData) => {
        // Player cards should already be dealt, just ensure UI is updated
        if (!playerCards.length && gameData.players) {
            const player = gameData.players.find(p => p.userId.toString() === user._id.toString());
            if (player) {
                setPlayerCards(player.hand || []);
            }
        }
    }, [playerCards.length, user]);

    const handleFlopPhase = useCallback((gameData) => {
        if (!isDealing) {
            setIsDealing(true);
            setTimeout(() => setIsDealing(false), 800);
        }
    }, [isDealing]);

    const handleTurnPhase = useCallback((gameData) => {
        if (!isDealing && gameData.communityCards.length >= 4) {
            setIsDealing(true);
            setTimeout(() => setIsDealing(false), 800);
        }
    }, [isDealing]);

    
    const handleRoundStart = useCallback((gameData) => {
      
        if (gameData.phase === "initial-deal") {
            
            setCommunityCards ([]);
            setDisplayedPot (0);
            setCurrentBet (0);
            setAnimatedChips ([]);
            const Player = gameData.players.find(p => p.userId.toString() === user._id.toString());
            if (Player && gameData.roundNumber >1){
            setPlayerCards([]);
            setOpponentHand([]);
            setTimeout(() => {
              setPlayerCards(Player.hand); 
              setOpponentHand(['hidden','hidden']);
            } , 800);
            }
           
        }
    }, [isDealing]);

    

    

    const handleRiverPhase = useCallback((gameData) => {
        if (!isDealing && gameData.communityCards.length >= 5) {
            setIsDealing(true);
            setTimeout(() => setIsDealing(false), 800);
        }
    }, [isDealing]);

    const handleShowdownPhase = useCallback((gameData) => {
        setIsDealing(false);
    }, [isDealing]);


const handleRoundEndPhase = useCallback((gameData) => {
  if (gameData.phase !== "round-complete") return;


  const betChips = animatedChipsRef.current.filter(c => c.stage === 'toBet');
  if (betChips.length > 0) {
    // Move all bet chips visually to pot
    const totalToAdd = betChips.reduce((sum, c) => sum + (c.amount || 0), 0);
    playPokerSound('moveToPot');
    moveChipsToPot(totalToAdd);
  }

  setTimeout(() => {
    if (gameData.roundWinner.length === 1) {
      const winner = gameData.roundWinner.toString() === user._id.toString()
        ? 'player'
        : 'opponent';
      const amount = gameData.pot;
      
      playPokerSound('moveToWinner');

      animatePotToWinner(winner, amount);
    } else {
      playPokerSound('moveToWinner');
      const amount = gameData.pot / 2;
      animatePotToWinner('player', amount);
      setTimeout(() => animatePotToWinner('opponent', amount), 200);
    }
  }, 1500); 

  setIsDealing(false);
}, [isDealing]);


    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        
        socket.on('connect', () => {
            fetchGameState();
        });
        
        socket.on('game-update', (data) => {
            if (!data.game) {
                navigate('/game');
                return; 
            }
            updateGameState(data.game, data.opponents || []);
        });

        socket.on('poker-message', (data) => {
            if (data.userID !== user._id) {
                setPokerOpponentMessage(t(`quickChat.${data.message}`));
                setTimeout(() => setPokerOpponentMessage(null), 2500);
            }
        });

        return () => {
            socket.off('game-update');
            socket.off('connect');
            socket.off('poker-message');
        };
    }, [socket, user, navigate, fetchGameState, updateGameState, t]);

    const handlePlayerAction = async (action, amount = 0) => {
        const isPlayerTurn = game.turn?.toString() === user._id.toString();
        if (!isPlayerTurn ) return;
        
        
        playPokerSound('buttonClick');
        setShowRaiseControl(false);
        setIsMoveLoading(true);
        if (action === 'check'){
        setPokerPlayerMessage(t('poker.check'));
        setTimeout(() => setPokerPlayerMessage(null), 1200);
        }
        
    
        try {
            const response = await apiRequest(`${API_BASE_URL}/api/poker-move`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    gameId: game._id,
                    action: action,
                    amount: amount,
                }),
            });
    
            if (!response.ok) throw new Error('Failed to perform action.');
            setShowActionButtons(false);
            setIsPlayerTurn(false);
        } catch (error) {
            console.error('Error sending action:', error);
        } finally {
            setIsMoveLoading(false);
        }
    };

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                fetchGameState();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchGameState]);

    useEffect(() => {
        fetchGameState();
    }, [fetchGameState]);

    const triggerConfettiAnimation = () => {
    const confettiContainer = document.createElement('div');
    confettiContainer.className = 'confetti-container';

    for (let i = 0; i < 200; i++) { 
        const confetti = document.createElement('div');
        confetti.className = 'confetti-piece';
        
      
        confetti.style.left = '50%';
        confetti.style.top = '50%'; 
        confetti.style.backgroundColor = `hsl(${Math.random() * 360}, 100%, 50%)`; 
        confetti.style.transform = `rotate(${Math.random() * 360}deg)`; 

  
        const randomX = (Math.random() - 0.5) * window.innerWidth * 2; 
        const randomY = (Math.random() - 0.5) * window.innerHeight * 2;
        const duration = Math.random() * 1.4 + 0.8; 

        confetti.style.animation = `explode ${duration}s ease-out forwards`;
        confetti.style.setProperty('--randomX', `${randomX}px`); 
        confetti.style.setProperty('--randomY', `${randomY}px`); 

        confettiContainer.appendChild(confetti);
    }

    document.body.appendChild(confettiContainer);

    setTimeout(() => {
        document.body.removeChild(confettiContainer);
    }, 4000);  
};


    useEffect(() => {
        if (game && user && isGameEnd) {
            if (game.winners?.includes(user._id)) {
                triggerConfettiAnimation();
                playPokerSound('win');
            } else {
                playPokerSound('lose');
            }
        }
    }, [isGameEnd, game, user._id, navigate]);
    
    useEffect(() => {
        if (isGameEnd) {
            updateUserDetails();
        }
    }, [isGameEnd, updateUserDetails]);
    
    const renderGameEndPopup = () => {
        if (!isGameEnd || !game || !game.players) return null;

        const currentPlayer = game.players.find(player => 
            player.userId.toString() === user._id.toString()
        );
        
        if (!currentPlayer) return null;

        const winnersArray = game.winners || [];
        const winnerId = winnersArray[0] || null;
        const isUserWinner = winnerId === user._id;
        const currency = currentPlayer.currency;
        const betAmount = currentPlayer.betAmount;
        const winnings = isUserWinner ? calculateWinnerGain(betAmount, currency) : 0;
        const endReason = game.endReason || null;

        const gamesPlayedToday = user.dailyGameStats?.[`${currency}GamesPlayed`] || 0;
        const gamesRemaining = Math.max(0, 10 - gamesPlayedToday);
        const showBonusOffer = gamesRemaining > 0;

        const getCurrencyIcon = () => {
            switch(currency) {
                case 'token':
                    return <div className={`poker-${isUserWinner ? 'Win' : 'Lose'}TokenCoin-icon`}></div>;
                case 'stars':
                    return <div className="poker-star-icon"></div>;
                case 'usdt':
                    return <div className="poker-dollar-sign-icon">$</div>;
                default:
                    return null;
            }
        };

        const getCurrencyBonusIcon = () => {
            switch(currency) {
                case 'token':
                    return <div className="poker-tokenYellow-icon"></div>;
                case 'stars':
                    return <div className="poker-star-icon"></div>;
                case 'usdt':
                    return <div className="poker-dollar-sign-icon">$</div>;
                default:
                    return null;
            }
        };

        const getBonusReward = () => {
            switch(currency) {
                case 'token': return 100;
                case 'stars': return 500;
                case 'usdt': return 10;
                default: return 0;
            }
        };

        const handleGetBonus = () => {
            setShowBonusDetails(true);
        };

        return (
            <>
                <div className="poker-popup-overlay"></div>
                <div className="poker-end-game-popup">
                    {isUserWinner && !isWaitingForRematch && <div className="poker-win-background-animated"></div>}
                    <div className="poker-screen-blur"></div>
                    <div className="poker-popup-background"></div>

                    {!isWaitingForRematch && (
                        <div className="poker-popup-content">
                            {!showBonusDetails ? (
                                <>
                                    <h2 className="poker-popup-title">
                                        {isUserWinner 
                                            ? (!endReason ? t('popup.youWon') : t('popup.opponentLeft'))
                                            : t('popup.youLose')}
                                    </h2>

                                    <div className="poker-points-change">
                                        {isUserWinner ? (
                                            <>
                                                <span className="poker-points-positive">
                                                    <span className="poker-sign">+</span>
                                                    <span className="poker-number">{winnings}</span>
                                                </span>
                                                {getCurrencyIcon()}
                                            </>
                                        ) : (
                                            <>
                                                <span className="poker-points-negative">
                                                    <span className="poker-sign">-</span>
                                                    <span className="poker-number">{betAmount}</span>
                                                </span>
                                                {getCurrencyIcon()}
                                            </>
                                        )}
                                    </div>

                                    <div className="poker-total-score">
                                        {t('labels.totalScore')}: <span>{user.score}</span>
                                    </div>

                                    <div className="poker-popup-buttons">
                                        <button 
                                            className="poker-exit-btn" 
                                            onPointerUp={(e) => handleExit(e)}
                                        />
                                        
                                        {showBonusOffer && (
                                            <button 
                                                className="poker-get-bonus-btn" 
                                                onPointerUp={handleGetBonus}
                                            >
                                                {t('buttons.getBonus')}
                                            </button>
                                        )}

                                        {!showBonusOffer && (
                                            <button 
                                                className="poker-replay-btn" 
                                                onPointerUp={(e) => handleReplay(e, game, user._id)}
                                            >
                                                {t('buttons.replay')}
                                            </button>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h2 className="poker-popup-title-small">
                                        {t('popup.playMoreGames', { count: gamesRemaining })}
                                    </h2>

                                    <div className="poker-points-change-bonus">
                                        <span className="poker-points-bonus">
                                            <span className="poker-sign">+</span>
                                            <span className="poker-number">{getBonusReward()}</span>
                                        </span>
                                        {getCurrencyBonusIcon()}
                                    </div>

                                    <div className="poker-popup-buttons">
                                        <button 
                                            className="poker-exit-btn" 
                                            onPointerUp={(e) => handleExit(e)}
                                        />
                                        
                                        <button 
                                            className="poker-play-btn" 
                                            onPointerUp={(e) => handleReplay(e, game, user._id)}
                                        >
                                            {t('buttons.play')}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {isWaitingForRematch && (
                        <div className="poker-popup-loading">
                            <div className="poker-loading-spinner"></div>
                            <div className="poker-waiting-text">{t('popup.waitingForOpponent')}</div>
                        </div>
                    )}
                </div>
            </>
        );
    };

    const calculateWinnerGain = (betAmount, currency) => {
    const payoutMap = {
    token: {
      25: 25
    },
    stars: {
      300: 200,
      1200: 800,
      6000: 4000
    },
    usdt: {
      3: 2,  
      12: 8,
      60: 40
    }
    };
  
    
        if (!payoutMap[currency]) {
            console.error(`Unsupported currency: ${currency}`);
            return 0;
        }
    
        const gain = payoutMap[currency][betAmount];
        if (gain === undefined) {
            console.error(`Invalid bet amount: ${betAmount} for currency: ${currency}`);
            return 0;
        }
    
        return gain;
    };


    const handleExit = (e) => {
        e.preventDefault(); 
        navigate('/game'); 
    };

    const handleReplay = async (e, currentGame, userid) => {
        e.preventDefault(); 
        if (!currentGame || !socket.id || !currentGame.players) return;
        
        const currentPlayer = currentGame.players.find(player => 
            player.userId.toString() === userid.toString() 
        );
        
        if (!currentPlayer) return;

        const gameMode = currentGame.gameMode;
        const socketId = socket.id;
        const amount = currentPlayer.betAmount;
        const currency = currentPlayer.currency;

        try {
            const response = await apiRequest(`${API_BASE_URL}/api/start-game`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    betAmount: amount, 
                    gameMode, 
                    socketId, 
                    isRematch: true,
                    currency ,
                    isPrived: !!currentGame.isPrivate, 
                    referrerId: currentGame.userIds?.[0] || null 
                })
            });
            const data = await response.json();
        
            if (response.ok) {
                setIsWaitingForRematch(true);
            } else {
                console.log("FAILED", data?.message);
            }
        } catch (err) {
            console.error("Rematch error:", err);
        }
    };

    const renderGameScore = () => {
        if (!game || !game.players) return null;
        
        const playerWins = game.players.find(p => p.userId.toString() === user._id.toString())?.isFolded === false ? 1 : 0;
        const opponent = opponents.find(o => o._id !== user._id);
        const opponentWins = opponent ? (game.players.find(p => p.userId.toString() === opponent._id.toString())?.isFolded === false ? 1 : 0) : 0;
        
        if (playerWins === 0 && opponentWins === 0) return null;
    
        return (
            <div className="poker-score-display">
                <div className="poker-score-box">{playerWins}</div>
                <div className="poker-score-separator">:</div>
                <div className="poker-score-box">{opponentWins}</div>
            </div>
        );
    };

    const renderAvatar = (name, photoUrl, customAvatar) => {
        if (customAvatar) {
            return <img src={customAvatar} alt={`${name}'s Custom Avatar`} className="poker-avatar-image" />;
        }
        if (photoUrl) {
            return <img src={photoUrl} alt={`${name}'s Avatar`} className="poker-avatar-image" />;
        }
        const initial = name?.charAt(0).toUpperCase() || "?"; 
        const avatarStyle = ABG(name);
        return <div className="poker-avatar-placeholder" style={avatarStyle}>{initial}</div>;
    };

    const renderOpponentArea = (game, opponents) => {
        const opponent = opponents.find(opponent => opponent._id !== user._id);
        if (!opponent || !game || !game.players) return null;
      
        const opponentPlayer = game.players.find(p => p.userId.toString() === opponent._id.toString());
        const isFolded = opponentPlayer?.isFolded || false;
        const anyPlayerFolded = game.players?.some(p => p.isFolded);
        const opponentStack = opponentPlayer?.stack || 0;
        const bettingPhases = ['pre-flop', 'flop', 'turn', 'river'];
        const isOpponentTurn = game?.turn === opponent._id && bettingPhases.includes(game.phase);
        
      
        return (
            <div className="poker-opponent-section">
                <div className="poker-opponent-wrapper">
                    <div className="poker-opponent-area">
                        <div className="poker-opponent-avatar">
                            <div className="poker-avatar-gradient-border"></div>
                            {renderAvatar(opponent.nickname, opponent.photoUrl, opponent.customAvatar)}
{game.turn === opponent._id && (
  <svg className="poker-timer-circleV">
    <circle className="poker-timer-base" cx="50%" cy="50%" r="45%" />
    <circle className="poker-timerO" cx="50%" cy="50%" r="43%" />
  </svg>
)}
                            {isFolded && <div className="poker-folded-overlay">{t('poker.folded')}</div>}
                        </div>

                        <div className={`poker-opponent-info ${isOpponentTurn ? 'poker-opponent-info-turn' : ''}`}>
                            <div className="poker-name-and-flag">
                                <span className="poker-opponent-name">{opponent.nickname}</span>
                                {opponent.country && (
                                    <img
                                        src={getFlag(opponent.country)}
                                        alt={`${opponent.country} flag`}
                                        className="poker-country-flag"
                                    />
                                )}
                            </div>
                            <span className="poker-opponent-score"> {t('labels.score')}: {opponent.score}</span>
                        </div>
            
                       <div className={`poker-opponent-steps ${animateOpponentStack ? 'winner-celebration' : ''}`} ref={opponentStackRef}>
  <div className="poker-current-round-section">
    <div className="poker-stack-icon"></div>
    <div className={`poker-round-points ${animateOpponentStack ? 'stack-number-pop' : ''}`}>
      {opponentStack}
    </div>
  </div>
</div>


                    {renderPokerOpponentMessage()}
 
                    </div>

                    
                    
                    
                    
                    <div className={`poker-opponent-pokerHand ${game.phase === 'initial-deal' ? 'poker-opponent-pokerHand-hidden' : ''}`}>
                       {opponentHand.map((card, index) => {

    const isWinningCard = (game.phase === 'round-complete' ) && game.winningCards.flat().includes(card) && !anyPlayerFolded ;
    const isOverlay = game.phase === 'round-complete' && !isWinningCard  ;
    const isOpponentFolded = game.phase === 'round-complete' && isFolded  ;
    
    return (
        <div 
            key={index} 
            className={`pokerCard pokerCard-entry-animation 
                        ${((game.phase === 'showdown' || game.phase === 'round-complete') && !isAnimatingBets && !anyPlayerFolded) ? 'pokerCard-opponent-flip-animation' : ''} 
                        ${isWinningCard ? 'pokerCard-opponent-winning' : ''} 
                        ${isOpponentFolded ? 'pokerCardBack-opponent-overlayed' : ''}
                        ${isOverlay ? 'pokerCard-opponent-overlayed' : ''}`}
        >
            <div className="pokerCard-inner">
                <div className="pokerCard-front">
                    <img 
                        src={pokerAssets.cards[card] || pokerAssets.cardBack} 
                        alt={card} 
                        className="poker-card-image"
                    />
                </div>
                <div className="pokerCard-back">
                    <img 
                        src={pokerAssets.cardBack} 
                        alt="back" 
                        className="poker-card-image"
                    />
                </div>
            </div>
        </div>
    );
})}


                    </div>
                </div>
            </div>
        );
    };







    const renderPokerPlayerArea = (game) => {
        if (!user || !game || !game.players) return null;
        
        const player = game.players.find(p => p.userId.toString() === user._id.toString());
        const isFolded = player?.isFolded || false;

        const bettingPhases = ['pre-flop', 'flop', 'turn', 'river'];
        const isPlayerTurn = game?.turn === user._id && bettingPhases.includes(game.phase);
    
        return (
            
            <div className="poker-player-section">
                
                <div className="poker-player-wrapper">
                    <div className="poker-player-area">
                       
                        <div className="poker-player-avatar">
                            <div className="poker-avatar-gradient-border"></div>
                            {renderAvatar(user.nickname, user.photoUrl, user.customAvatar)}
{game.turn === user._id && (
  <svg className="poker-timer-circleV">
    <circle className="poker-timer-base" cx="50%" cy="50%" r="45%" />
    <circle className="poker-timerP" cx="50%" cy="50%" r="43%" />
  </svg>
)}
                            {isFolded && <div className="poker-folded-overlay">{t('poker.folded')}</div>}
                        </div>
        
                        <div className={`poker-player-info ${isPlayerTurn ? 'poker-player-info-turn' : ''}`}>
                            <div className="poker-name-and-flag">
                                <span className="poker-player-name">{user.nickname}</span>
                                {user.country && (
                                    <img
                                        src={getFlag(user.country)}
                                        alt="flag"
                                        className="poker-country-flag"
                                    />
                                )}
                            </div>
                            <span className="poker-player-score">{t('labels.score')}: {user.score}</span>
                        </div>





        
               <div className={`poker-player-steps ${animatePlayerStack ? 'winner-celebration' : ''}`}>
  <div className="poker-current-round-section">
    <div className="poker-stack-icon" ref={playerStackRef}></div>
    <div className={`poker-round-points ${animatePlayerStack ? 'stack-number-pop' : ''}`}>
      {playerStack}
    </div>
  </div>
</div>
                         
                        
           
                        {renderPokerPlayerMessage()}
                      
                    </div>
                    
                    
                    {renderPokerControlButtons()}
                   
                    {renderActionButtons()}
                </div>
                
                
               <div className={`poker-player-pokerHand ${game.phase === 'initial-deal' ? 'poker-player-pokerHand-hidden' : ''}`}>
{playerCards.map((card, index) => {
    const anyPlayerFolded = game.players?.some(p => p.isFolded);
    const currentPlayer = game?.players?.find(p => p.userId.toString() === user._id.toString());
    const isBestHandCard = game.phase !== 'round-complete' && currentPlayer?.bestHand?.mainCards?.includes(card);
    const isWinningCard = game.phase === 'round-complete' && game.winningCards.flat().includes(card) && !anyPlayerFolded;
    const isOverlay = game.phase === 'round-complete' && !isWinningCard && !anyPlayerFolded;
    const isPlayerFolded = game.phase === 'round-complete' && isFolded;
    const isPlayerWinnerAfterFold = game.phase === 'round-complete' && anyPlayerFolded && game.roundWinner.toString() === user._id.toString();
    

    return (
        <div 
            key={index} 
            className={`pokerCard pokerCard-entry-animation 
                        ${game.phase !== 'initial-deal' ? 'pokerCard-flip-animation' : ''} 
                        ${isBestHandCard ? 'pokerCard-best-hand' : ''} 
                        ${isWinningCard ? 'pokerCard-winning' : ''}
                        ${isPlayerFolded ? 'pokerCard-overlayed' : ''} 
                        ${isPlayerWinnerAfterFold ? 'pokerCard-winning' : ''} 
                        ${isOverlay ? 'pokerCard-overlayed' : ''}`}
        >
            <div className="pokerCard-inner">
                <div className="pokerCard-front">
                    <img 
                        src={pokerAssets.cards[card] || pokerAssets.cardBack} 
                        alt={card} 
                        className="poker-card-image"
                    />
                </div>
                <div className="pokerCard-back">
                    <img 
                        src={pokerAssets.cardBack} 
                        alt="back" 
                        className="poker-card-image"
                    />
                </div>
            </div>
        </div>
    );
})}


</div>
                  
            </div>
            
        );
    };



    const renderPopup = () => {
        if (game?.status === 'canceled') {
            return (
                <div className="popup">
                    <div className="popup-inner">
                        <h3>{t('popup.gameCanceled')}</h3>
                        <p>{t('popup.cancelReason')}</p>
                        <button onClick={() => navigate('/game')}>
                            {t('buttons.close')}
                        </button>
                    </div>
                </div>
            );
        }
        return null;
    };


    const renderCommunityCards = () => {
    
        if (!game) return ;
        const anyPlayerFolded = game.players?.some(p => p.isFolded);

    return (
        <div className="poker-table-area" ref={playAreaRef}>
            {renderPotAmount()}
            <div className="poker-community-pokerCards">
                {communityCards.map((card, index) => {
                    const currentPlayer = game?.players?.find(p => p.userId.toString() === user._id.toString());
                    const isBestHandCard = game.phase !== 'round-complete' && game.phase !== 'showdown' &&
                                           currentPlayer?.bestHand?.mainCards?.includes(card);

                    const isWinningCard = game.phase === 'round-complete' &&
                                          game.winningCards.flat().includes(card) && !anyPlayerFolded;

                    const isOverlay = game.phase === 'round-complete' && !isWinningCard && !anyPlayerFolded;

                    return (
    <div 
        key={index} 
        className={`poker-community-pokerCard ${isBestHandCard ? 'poker-community-card-best-hand' : ''}`}
        style={{ "--card-index": index }}
    >
        <div className={`poker-community-card-wrapper ${isWinningCard ? 'poker-community-card-winning' : ''}`}>
            <div className={`poker-community-card-inner ${game.phase === 'flop' ? 'poker-community-flop-flip' : ''}`}>
                <div className="poker-community-card-front">
                    <img 
                        src={pokerAssets.cards[card]} 
                        alt={card} 
                        className="poker-card-image"
                    />
                </div>
                <div className="poker-community-card-back">
                    <img 
                        src={pokerAssets.cardBack} 
                        alt="card back" 
                        className="poker-card-image"
                    />
                </div>
            </div>
            {isOverlay && <div className="poker-community-card-overlay" />}
        </div>
    </div>
);
                })}
            </div>
            <div style={{height: '20px', visibility: 'hidden'}}></div>
        </div>
    );
};





const openRaiseControl = (buttonEl, minStart) => {
  if (!buttonEl) {
    setShowRaiseControl(true);
    setRaiseControllerPos({ left: null, top: null });
    setRaiseAmount(minStart);
    return;
  }
  const rect = buttonEl.getBoundingClientRect();

  const left = Math.max(8, rect.left - 130);
  const top = Math.max(8, rect.top + rect.height / 2 - 120); 
  setRaiseControllerPos({ left, top });
  setRaiseAmount(minStart);
  setShowRaiseControl(true);
};

// close controller (cancel)
const closeRaiseControl = () => {
  setShowRaiseControl(false);
};

// confirm and send raise
const confirmRaise = () => {
  if (!game) return;
  handlePlayerAction('raise', raiseAmount);
  setShowRaiseControl(false);
};


  const renderRaiseControl = () => {
  if (!showRaiseControl || !game) return null;

  const player = game.players.find(p => p.userId.toString() === user._id.toString());
  if (!player) return null;

  const opponent = game.players.find(p => p.userId.toString() !== user._id.toString());
  if (!opponent || opponent.isFolded) return null;

  const callAmount = Math.max(0, (game.currentBet || 0) - (player.chipsBetThisRound || 0));

  const lastRaiseSize = (() => {
    const la = game.lastAction;
    if (la && la.type === "raise" && typeof la.amount === "number" && la.amount > 0) return la.amount;
    return game.bigBlind || 1;
  })();

  const opponentMaxPossible = (opponent.chipsBetThisRound || 0) + (opponent.stack || 0) - (player.chipsBetThisRound || 0) ;
  const playerAbsoluteMax = (player.chipsBetThisRound || 0) + (player.stack || 0);

  let minRaise = (player.chipsBetThisRound || 0) + callAmount + lastRaiseSize;
  let maxRaise = Math.min(playerAbsoluteMax, opponentMaxPossible);
  console.log("llllllll",opponent.chipsBetThisRound, opponent.stack, opponentMaxPossible)

  if (isNaN(minRaise) || minRaise < (player.chipsBetThisRound || 0)) minRaise = (player.chipsBetThisRound || 0);
  if (isNaN(maxRaise) || maxRaise < (player.chipsBetThisRound || 0)) maxRaise = (player.chipsBetThisRound || 0);
  if (minRaise > maxRaise) minRaise = maxRaise;

  if (typeof raiseAmount === "undefined" || raiseAmount < minRaise || raiseAmount > maxRaise) {
    const newVal = Math.max(minRaise, Math.min(raiseAmount || minRaise, maxRaise));
    setRaiseAmount(newVal);
  }

  const step = Math.max(1, game.bigBlind || 1);
  const range = Math.max(0, maxRaise - minRaise);
  const steps = range > 0 ? Math.floor(range / step) : 0;
  const lastStep = minRaise + steps * step;
  const sliderStep = lastStep < maxRaise ? Math.max(1, Math.round(maxRaise - lastStep)) : step;

  const rawPercentage = range === 0 ? 1 : (raiseAmount - minRaise) / (range || 1);
  const trackPadding = 0.15;
  const adjustedPercentage = trackPadding + rawPercentage * (1 - trackPadding * 2);
  const positionPercentage = 100 - adjustedPercentage * 100;

  const submitRaise = (amount) => {
    const clamped = Math.max(minRaise, Math.min(amount, maxRaise));
    setRaiseAmount(clamped);
    // Example: socket.emit('player_raise', { gameId: game._id, amount: clamped });
  };

  return (
    <div className="raise-controller-root">
      <div
        className="raise-screen-overlay"
        onPointerUp={() => closeRaiseControl()}
        onTouchEnd={() => closeRaiseControl()}
      />
      <div
        className="raise-controller"
        onPointerUp={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <div className="raise-slider-container">
          <div
            className="raise-amount-label"
            style={{ top: `${positionPercentage}%` }}
          >
            {raiseAmount >= maxRaise ? "all-in" : raiseAmount}
          </div>
          <div className="raise-vertical-slider-wrap">
            <input
              type="range"
              className="raise-vertical-slider"
              min={minRaise}
              max={maxRaise}
              step={sliderStep}
              value={raiseAmount}
              onChange={(e) => {
                const v = Number(e.target.value);
                const clamped = Math.max(minRaise, Math.min(v, maxRaise));
                setRaiseAmount(clamped);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onPointerUp={(e) => {
                e.stopPropagation();
                setRaiseAmount(prev => Math.max(minRaise, Math.min(prev, maxRaise)));
                submitRaise(raiseAmount);
              }}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchMove={(e) => {
                e.stopPropagation();
                const v = Number(e.target.value);
                setRaiseAmount(Math.max(minRaise, Math.min(v, maxRaise)));
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                submitRaise(raiseAmount);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};



const [actionInProgress, setActionInProgress] = useState(false);

const renderActionButtons = () => {
  const player = game.players.find(p => p.userId.toString() === user._id.toString());
  const opponent = game.players.find(p => p.userId.toString() !== user._id.toString());
  if (!player || !opponent) return null;

  const opponentTotalPossible  = (opponent?.chipsBetThisRound || 0) + (opponent?.stack || 0);
  const bettingPhases = ['pre-flop', 'flop', 'turn', 'river'];
  const isPlayerTurn = game.turn?.toString() === user._id.toString() && bettingPhases.includes(game.phase);
  const callAmount = Math.max(0, (game.currentBet || 0) - (player.chipsBetThisRound || 0));
  const canCheck = game.currentBet === 0 || callAmount === 0;
  const canRaise = (player.stack || 0) > 0 && ((player.stack || 0) > callAmount ) && (opponentTotalPossible > callAmount + player?.chipsBetThisRound);

  const lastRaiseSize = Math.max(game.bigBlind || 0, (game.currentBet || 0) - (game.previousBet || 0));
  let minRaise = (player.chipsBetThisRound || 0) + callAmount + lastRaiseSize;
  let maxRaise = (player.chipsBetThisRound || 0) + player.stack;
  if (minRaise > maxRaise) minRaise = maxRaise;

  const handleOpenRaise = (e) => {
    e.stopPropagation();
    if (!isPlayerTurn || actionInProgress) return;
    openRaiseControl(e.currentTarget, minRaise);
  };

  const handleAction = (action) => {
    if (!isPlayerTurn || actionInProgress) return;
    setActionInProgress(true);
    handlePlayerAction(action);
    setTimeout(() => setActionInProgress(false), 300);
  };

  const handleConfirmRaise = (e) => {
    e.stopPropagation();
    if (!isPlayerTurn || actionInProgress) return;
    setActionInProgress(true);
    confirmRaise();
    setTimeout(() => setActionInProgress(false), 300);
  };

  return (
    <div className="poker-action-buttons">
      <div className={`poker-buttons-container ${!isPlayerTurn ? 'inactive' : ''}`}>
        <button
          className="base-item-box poker-action-btn poker-fold-btn"
          onPointerUp={() => handleAction('fold')}
          disabled={!isPlayerTurn || actionInProgress}
        >
          <span className="base-item-content poker-action-btn">{t('poker.fold')}</span>
        </button>

        <button
          className="base-item-box poker-action-btn poker-check-call-btn"
          onPointerUp={() => handleAction(canCheck ? 'check' : 'call')}
          disabled={!isPlayerTurn || actionInProgress}
        >
          <span className="base-item-content poker-action-btn">
            {canCheck && isPlayerTurn ? t('poker.check') :
              !isPlayerTurn ? t('poker.check') :
                `${t('poker.call')} ${Math.max(0, callAmount)}`}
          </span>
        </button>

        {canRaise && (
          !showRaiseControl ? (
            <button
              className="base-item-box poker-action-btn poker-raise-btn"
              onPointerUp={handleOpenRaise}
              disabled={!isPlayerTurn || actionInProgress}
            >
              <span className="base-item-content poker-action-btn">{t('poker.raise')}</span>
            </button>
          ) : (
            <button
              className="base-item-box poker-action-btn poker-raise-btn active-confirm"
              onPointerUp={handleConfirmRaise}
              disabled={!isPlayerTurn || actionInProgress}
            >
              <span className="base-item-content poker-action-btn">
                {raiseAmount >= maxRaise 
                  ? 'all-in' 
                  : `${t('poker.raise')} ${raiseAmount}`}
              </span>
            </button>
          )
        )}
      </div>
      {renderRaiseControl()}
    </div>
  );
};



const renderPotAmount = () => {
  return (
    <div className="poker-pot-display" ref={potRef}>
      <div
        className="poker-pot-inner"
        style={{ opacity: displayedPot > 0 ? 1 : 0 }}
      >
        <div className="poker-chip-icon poker-chip-yellow"></div>
        <div className="poker-pot-amount">
          <span className="pot-number" key={displayedPot}>
            {displayedPot}
          </span>
        </div>
      </div>
    </div>
  );
};








    const renderPokerControlButtons = () => {
    if (!game) return null;

    return (
        <div className="poker-game-buttonsIcons">
            {/* Text Button */}
            <button
                className="poker-textIcon-buttonIcon"
                onPointerDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setPokerShowTextMenu(!pokerShowTextMenu);
                }}
            >
                <img src={commonIcons.textIcon} alt="Text" />
            </button>

            {pokerShowTextMenu && (
                <div className="poker-dropdown-menu poker-text-menu">
                    <ul>
                        <li onPointerUp={(e) => { e.stopPropagation(); handlePokerMessageSelect('hello'); }}>
                            {t('quickChat.hello')}
                        </li>
                        <li onPointerUp={(e) => { e.stopPropagation(); handlePokerMessageSelect('goodLuck'); }}>
                            {t('quickChat.goodLuck')}
                        </li>
                        <li onPointerUp={(e) => { e.stopPropagation(); handlePokerMessageSelect('wellPlayed'); }}>
                            {t('quickChat.wellPlayed')}
                        </li>
                        <li onPointerUp={(e) => { e.stopPropagation(); handlePokerMessageSelect('thanks'); }}>
                            {t('quickChat.thanks')}
                        </li>
                        <li onPointerUp={(e) => { e.stopPropagation(); handlePokerMessageSelect('oops'); }}>
                            {t('quickChat.oops')}
                        </li>
                        <li onPointerUp={(e) => { e.stopPropagation(); handlePokerMessageSelect('tooEasy'); }}>
                            {t('quickChat.tooEasy')}
                        </li>
                        <li onPointerUp={(e) => { e.stopPropagation(); handlePokerMessageSelect('hurryUp'); }}>
                            {t('quickChat.hurryUp')}
                        </li>
                    </ul>
                </div>
            )}

            {/* Settings Button */}
            <button
                className={`poker-settingsIcon-buttonIcon ${pokerShowSettingsMenu ? "active" : ""}`}
                onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPokerShowSettingsMenu(!pokerShowSettingsMenu);
                }}
            >
                <img src={commonIcons.settingsIcon} alt="Settings" />
            </button>

            {/* Extra Buttons (Only Show When Settings is Active) */}
            {pokerShowSettingsMenu && (
                <div className="poker-settings-expanded">
                    {/* Leave Game Button */}
                    <button
                        className="poker-leaveGame-buttonExt"
                        onPointerUp={(e) => {
                            handleLeaveGame(e);
                        }}
                    >
                        <img src={commonIcons.exitIcon} alt="Leave Game" />
                    </button>

                    {/* Sound Toggle Button */}
                    <button
                        className="poker-soundToggle-buttonExt"
                        onPointerDown={(e) => {
                            e.stopPropagation();
                            handleSoundToggle(e);
                            e.preventDefault();
                        }}
                    >
                        <img src={isSoundOn ? commonIcons.soundIcon : commonIcons.soundOffIcon} alt="Toggle Sound" />
                    </button>
                </div>
            )}
        </div>
    );
};
      
    const renderPokerPlayerMessage = () => {
        return (
            pokerPlayerMessage && (
                <div className="poker-message-popup poker-player-message">
                    <div className="poker-message-popup-inner poker-player-message-inner">
                        {pokerPlayerMessage}
                    </div>
                </div>
            )
        );
    };
    
    const renderPokerOpponentMessage = () => {
        return (
            pokerOpponentMessage && (
                <div className="poker-message-popup poker-opponent-message">
                    <div className="poker-message-popup-inner poker-opponent-message-inner">
                        {pokerOpponentMessage}
                    </div>
                </div>
            )
        );
    };
    
    const renderLeaveGamePopup = () => {
        if (!isLeaveGamePopupVisible || isGameEnd) return null;
        
        return (
            <>
                <div className="poker-popup-overlay"></div>
                <div className="poker-screen-blur" />
      
                <div className="poker-end-game-popup">
                    <div className="poker-popup-background" />
                    <div className="poker-leave-popup-content">
                        <div className="poker-leave-popup-title">
                            {t('leaveGame.warning')}
                        </div>
      
                        <div className="poker-leave-popup-buttons">
                            <button
                                className="poker-leave-popup-btn"
                                onPointerUp={(e) => handleConfirmLeave(e, game?._id)}
                            >
                                {t('buttons.leave')}
                            </button>
                            <button
                                className="poker-leave-popup-btn"
                                onPointerUp={(e) => handlePopupClose(e)}
                            >
                                {t('buttons.cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    };
    
    const handlePokerMessageSelect = (message) => {
        setPokerPlayerMessage(t(`quickChat.${message}`));
        setTimeout(() => setPokerPlayerMessage(null), 2500);
    
        socket.emit('poker-message', { message, gameId: game._id, userID: user._id });
        setPokerShowTextMenu(false);
    };
    
    const handleSoundToggle = (e) => {
        e.stopPropagation();
        setIsSoundOn((prev) => !prev);
    };
    
    const handleLeaveGame = (e) => {
        e.stopPropagation();
        setIsLeaveGamePopupVisible(true);
    };
    
    const handlePopupClose = (e) => {
        e.preventDefault();
        setIsLeaveGamePopupVisible(false);
    };
    
    const handleConfirmLeave = async (e, gameId) => {
        e.preventDefault();
    
        if (!gameId) {
            setIsLeaveGamePopupVisible(false);
            return;
        } 
       
        try {
            const response = await apiRequest(`${API_BASE_URL}/api/leave-poker-game`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ gameId }),
            });
    
            if (response.ok) {
                setIsLeaveGamePopupVisible(false);
                navigate('/game');
            } else {
                console.error('Failed to leave game');
            }
        } catch (error) {
            console.error('Error leaving game:', error);
        }
    };

    if (!game) {
        return (
            <div className="poker-container">
                <h2>{t('loading.loadingGame')}</h2>
            </div>
        );
    }




    return (
        <div className="poker-container">
      <div ref={chipLayerRef} className="chip-layer" aria-hidden="true">
  {animatedChips.map(ch => (
    <div
      key={ch.id} 
      className={`animated-chip ${ch.stage}`}
      style={{
        left: `${ch.x}px`,
        top: `${ch.y}px`,
        transform: `translate(-50%, -50%) translate(${ch.dx}px, ${ch.dy}px)`,
        transition: `transform ${ch.duration}ms cubic-bezier(.22,.9,.29,1), opacity ${ch.duration}ms ease`,
        zIndex: 5000,
        position: 'fixed',
        pointerEvents: 'none',
      }}
    >
      <div className="poker-chip-icon poker-chip-yellow"></div>
      {ch.amount ? <div className="chip-amount">{ch.amount}</div> : null}
    </div>
  ))}
</div>

            {renderOpponentArea(game, opponents)}
            
            <div className="poker-table">
            {renderCommunityCards()}
            </div>

  

            {renderPokerPlayerArea(game)}
           
            
        
            {renderGameEndPopup()}
            {renderLeaveGamePopup()}
            {renderGameScore()}
            {renderPopup()}
        </div>
    );
};

export default PokerGame;